import { useEffect, useImperativeHandle, useRef, type Ref } from "react";

export type MusicTransport = Pick<HTMLAudioElement, "play" | "pause" | "paused" | "currentTime" | "duration" | "volume" | "playbackRate">;
type WidgetEvent = { currentPosition?: number };
type Widget = {
  bind: (event: string, listener: (event?: WidgetEvent) => void) => void;
  unbind: (event: string) => void;
  play: () => void; pause: () => void; seekTo: (milliseconds: number) => void;
  setVolume: (volume: number) => void; getDuration: (callback: (duration: number) => void) => void;
};
type WidgetFactory = ((frame: HTMLIFrameElement) => Widget) & { Events: Record<string, string> };
type SoundCloudWindow = Window & { SC?: { Widget?: WidgetFactory } };
let loadingApi: Promise<WidgetFactory> | null = null;

function widgetApi(): Promise<WidgetFactory> {
  const existing = (window as SoundCloudWindow).SC?.Widget;
  if (existing) return Promise.resolve(existing);
  if (loadingApi) return loadingApi;
  loadingApi = new Promise<WidgetFactory>((resolve, reject) => {
    const script = document.createElement("script");
    const timer = window.setTimeout(() => { script.remove(); reject(new Error("SoundCloud’s player did not load. Check VeePN is connected.")); }, 20_000);
    script.src = "https://w.soundcloud.com/player/api.js";
    script.async = true;
    script.onload = () => {
      clearTimeout(timer);
      const factory = (window as SoundCloudWindow).SC?.Widget;
      if (factory) resolve(factory);
      else reject(new Error("SoundCloud’s player API is unavailable"));
    };
    script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error("SoundCloud’s player could not be reached. Check VeePN is connected.")); };
    document.head.append(script);
  }).catch((error) => { loadingApi = null; throw error; });
  return loadingApi;
}

export function SoundCloudWidget({ trackId, title, duration, wantsPlayback, volume, startTime, transportRef, onReady, onPlay, onPause, onTime, onDuration, onEnded, onError }: {
  trackId: string; title: string; duration: number; wantsPlayback: boolean; volume: number; startTime: number;
  transportRef: Ref<MusicTransport>;
  onReady: () => void; onPlay: () => void; onPause: () => void; onTime: (seconds: number) => void;
  onDuration: (seconds: number) => void; onEnded: () => void; onError: (message: string) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const widgetRef = useRef<Widget | null>(null);
  const readyRef = useRef(false);
  const state = useRef({ time: startTime, duration, volume, paused: true, wantsPlayback });
  const callbacks = useRef({ onReady, onPlay, onPause, onTime, onDuration, onEnded, onError });
  callbacks.current = { onReady, onPlay, onPause, onTime, onDuration, onEnded, onError };
  state.current.wantsPlayback = wantsPlayback;
  state.current.volume = volume;

  useImperativeHandle(transportRef, () => ({
    get currentTime() { return state.current.time; },
    set currentTime(value: number) { state.current.time = value; if (readyRef.current) widgetRef.current?.seekTo(value * 1000); callbacks.current.onTime(value); },
    get duration() { return state.current.duration; },
    get volume() { return state.current.volume; },
    set volume(value: number) { state.current.volume = value; if (readyRef.current) widgetRef.current?.setVolume(value * 100); },
    get paused() { return state.current.paused; },
    get playbackRate() { return 1; },
    set playbackRate(_value: number) { /* The official widget plays at normal speed. */ },
    play: async () => { state.current.wantsPlayback = true; if (readyRef.current) widgetRef.current?.play(); },
    pause: () => { state.current.wantsPlayback = false; if (readyRef.current) widgetRef.current?.pause(); }
  }), []);

  useEffect(() => {
    let cancelled = false;
    let widget: Widget | undefined;
    let events: string[] = [];
    readyRef.current = false;
    state.current.time = startTime;
    state.current.duration = duration;
    state.current.paused = true;
    void widgetApi().then((factory) => {
      if (cancelled || !frameRef.current) return;
      widget = factory(frameRef.current);
      widgetRef.current = widget;
      const bind = (name: string, listener: (event?: WidgetEvent) => void) => {
        const eventName = factory.Events[name];
        if (eventName) { events.push(eventName); widget!.bind(eventName, (event) => { if (!cancelled) listener(event); }); }
      };
      bind("READY", () => {
        readyRef.current = true;
        widget!.setVolume(state.current.volume * 100);
        if (state.current.time > 0) widget!.seekTo(state.current.time * 1000);
        widget!.getDuration((milliseconds) => { if (!cancelled && Number.isFinite(milliseconds)) { state.current.duration = milliseconds / 1000; callbacks.current.onDuration(milliseconds / 1000); } });
        callbacks.current.onReady();
        if (state.current.wantsPlayback) widget!.play();
      });
      bind("PLAY", () => { state.current.paused = false; callbacks.current.onPlay(); });
      bind("PAUSE", () => { state.current.paused = true; callbacks.current.onPause(); });
      const progress = (event?: WidgetEvent) => { if (Number.isFinite(event?.currentPosition)) { state.current.time = Number(event!.currentPosition) / 1000; callbacks.current.onTime(state.current.time); } };
      bind("PLAY_PROGRESS", progress);
      bind("SEEK", progress);
      bind("FINISH", () => { state.current.paused = true; callbacks.current.onEnded(); });
      bind("ERROR", () => { state.current.paused = true; callbacks.current.onError("SoundCloud could not play this track. Try its embedded play button or open it on SoundCloud."); });
    }).catch((error) => { if (!cancelled) callbacks.current.onError(error instanceof Error ? error.message : "SoundCloud player unavailable"); });
    return () => {
      cancelled = true;
      readyRef.current = false;
      if (widget) { for (const event of events) widget.unbind(event); widget.pause(); }
      widgetRef.current = null;
    };
  }, [trackId]);

  useEffect(() => { if (readyRef.current) widgetRef.current?.setVolume(volume * 100); }, [volume]);
  useEffect(() => { if (readyRef.current) { if (wantsPlayback) widgetRef.current?.play(); else widgetRef.current?.pause(); } }, [wantsPlayback]);

  const source = `https://w.soundcloud.com/player/?url=${encodeURIComponent(`https://api.soundcloud.com/tracks/${trackId}`)}&auto_play=false&show_artwork=false&show_comments=false&show_user=true&sharing=false&buying=false&download=false&visual=false&color=%23ff5c17`;
  return <section className="soundcloud-remote-player"><strong>Streaming from SoundCloud</strong><span>This track is not downloaded yet. Playback uses your browser’s SoundCloud connection.</span><iframe key={trackId} ref={frameRef} src={source} allow="autoplay" title={`SoundCloud playback: ${title}`} scrolling="no" /></section>;
}
